import { useState, useEffect } from 'react';

function useCountdown(storageKey: string, durationHours: number) {
  const getInitialTime = () => {
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      const remaining = parseInt(saved, 10) - Date.now();
      if (remaining > 0) return Math.floor(remaining / 1000);
    }
    const duration = durationHours * 60 * 60;
    localStorage.setItem(storageKey, String(Date.now() + duration * 1000));
    return duration;
  };

  const [seconds, setSeconds] = useState(getInitialTime);

  useEffect(() => {
    if (seconds <= 0) return;
    const interval = setInterval(() => {
      setSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const h = String(Math.floor(seconds / 3600)).padStart(2, '0');
  const m = String(Math.floor((seconds % 3600) / 60)).padStart(2, '0');
  const s = String(seconds % 60).padStart(2, '0');

  return { h, m, s, expired: seconds <= 0 };
}

export default function Oferta() {
  const { h, m, s, expired } = useCountdown('oferta_countdown', 3);

  const itens = [
    'Treinamento completo em vídeo',
    'Acesso online imediato',
    'Acesso Vitalício',
    'Conteúdo prático e aplicável',
    'PDF das aulas',
    'Atualizações gratuitas',
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <div className="bg-gradient-to-br from-teal-50 to-teal-100/50 border-2 border-teal-200 rounded-3xl p-6 sm:p-10 shadow-2xl">
          {/* Título */}
          <h2 className="text-3xl lg:text-4xl font-bold text-center text-gray-900 mb-8">
            Acesso ao Protocolo de Prevenção de Crises
          </h2>

          {/* Lista */}
          <div className="space-y-4 mb-10">
            {itens.map((item, index) => (
              <div key={index} className="flex items-center gap-4 bg-white rounded-xl p-4 shadow-sm">
                <div className="w-8 h-8 flex items-center justify-center bg-teal-100 rounded-full flex-shrink-0">
                  <i className="ri-checkbox-circle-fill text-teal-600 text-xl"></i>
                </div>
                <p className="text-gray-800 text-base font-medium">{item}</p>
              </div>
            ))}
          </div>

          {/* Contador regressivo */}
          <div className="mb-8">
            <div className="bg-red-50 border border-red-200 rounded-2xl p-5 text-center">
              <div className="flex items-center justify-center gap-2 mb-3">
                <i className="ri-alarm-warning-fill text-red-500 text-lg"></i>
                <p className="text-red-600 font-semibold text-sm uppercase tracking-wide">
                  {expired ? 'Oferta encerrada!' : 'Oferta por tempo limitado!'}
                </p>
              </div>
              {!expired ? (
                <>
                  <div className="flex items-center justify-center gap-2">
                    {[{ label: 'Horas', value: h }, { label: 'Min', value: m }, { label: 'Seg', value: s }].map(({ label, value }, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <div className="bg-red-600 text-white rounded-xl px-3 sm:px-4 py-3 min-w-[52px] sm:min-w-[64px]">
                          <p className="text-2xl sm:text-3xl font-bold leading-none tabular-nums">{value}</p>
                          <p className="text-xs text-red-200 mt-1">{label}</p>
                        </div>
                        {i < 2 && <span className="text-red-600 text-xl sm:text-2xl font-bold">:</span>}
                      </div>
                    ))}
                  </div>
                  <p className="text-gray-500 text-xs mt-3">
                    Após esse prazo, o preço volta para R$ 997,00
                  </p>
                </>
              ) : (
                <p className="text-red-500 text-sm">O preço promocional expirou.</p>
              )}
            </div>
          </div>

          {/* Preço */}
          <div className="text-center space-y-4 mb-8">
            <div className="space-y-2">
              <p className="text-gray-600 text-base line-through">De R$ 197,00</p>
              <div className="flex items-center justify-center gap-3">
                <p className="text-5xl font-bold text-teal-600">R$ 87,90</p>
                <div className="text-left">
                  <p className="text-sm text-gray-600">ou 12x de</p>
                  <p className="text-xl font-bold text-gray-900">R$ 9,09</p>
                </div>
              </div>
            </div>
          </div>

          {/* Botão */}
          <div className="text-center">
            <a
              href="https://pay.hotmart.com/U103348916W?checkoutMode=10"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block w-full sm:w-auto bg-teal-600 hover:bg-teal-700 text-white font-bold px-8 py-5 rounded-xl text-lg sm:text-xl transition-all duration-300 shadow-lg hover:shadow-2xl cursor-pointer"
            >
              Quero acessar o protocolo agora
            </a>
            <p className="text-gray-600 text-sm mt-4">
              Acesso imediato após a confirmação do pagamento
            </p>

            {/* Selos de segurança e pagamento */}
            <div className="mt-5 flex flex-col items-center gap-3">
              <div className="flex items-center gap-2 text-gray-500 text-xs">
                <i className="ri-lock-fill text-green-600 text-base"></i>
                <span className="text-green-700 font-semibold">Compra 100% segura e criptografada</span>
              </div>
              <div className="flex items-center justify-center gap-3 flex-wrap">
                {/* Visa */}
                <div className="bg-white border border-gray-200 rounded-lg px-3 py-2 flex items-center gap-1 shadow-sm">
                  <span className="text-blue-800 font-extrabold text-sm tracking-tight italic">VISA</span>
                </div>
                {/* Mastercard */}
                <div className="bg-white border border-gray-200 rounded-lg px-3 py-2 flex items-center gap-1 shadow-sm">
                  <div className="flex items-center">
                    <div className="w-5 h-5 rounded-full bg-red-500 opacity-90"></div>
                    <div className="w-5 h-5 rounded-full bg-yellow-400 opacity-90 -ml-2"></div>
                  </div>
                  <span className="text-gray-700 font-bold text-xs ml-1">Mastercard</span>
                </div>
                {/* Pix */}
                <div className="bg-white border border-gray-200 rounded-lg px-3 py-2 flex items-center gap-1 shadow-sm">
                  <i className="ri-qr-code-line text-teal-500 text-base"></i>
                  <span className="text-teal-600 font-bold text-sm">Pix</span>
                </div>
                {/* Boleto */}
                <div className="bg-white border border-gray-200 rounded-lg px-3 py-2 flex items-center gap-1 shadow-sm">
                  <i className="ri-barcode-line text-gray-600 text-base"></i>
                  <span className="text-gray-600 font-bold text-xs">Boleto</span>
                </div>
                {/* Cadeado SSL */}
                <div className="bg-white border border-gray-200 rounded-lg px-3 py-2 flex items-center gap-1 shadow-sm">
                  <i className="ri-shield-check-fill text-green-500 text-base"></i>
                  <span className="text-gray-600 font-bold text-xs">SSL</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
